'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSession } from 'next-auth/react';
import { useSearchParams, useRouter } from 'next/navigation';
import { BookOpen, Sparkles, Download, Loader2, CreditCard, Check, Zap, Heart, Palette, Crown, Star } from 'lucide-react';
import { MagicalGenerationModal } from './magical-generation-modal';
import { InsufficientCreditsModal } from './insufficient-credits-modal';
import { PurchaseSuccessModal } from './purchase-success-modal';
import { RateLimitModal } from './rate-limit-modal';
import { BooksGallery } from './books-gallery';
import { MagicalStoryModal } from './magical-story-modal';
import AudioBookVideoPlayer from './audio-book/audio-book-video-player';
import MontessoriToggle from './montessori/MontessoriToggle';
import MontessoriFocusSelector from './montessori/MontessoriFocusSelector';
import MontessoriUpsellModal from './montessori/MontessoriUpsellModal';
import { FeaturedAudioBooks } from './featured-audio-books';
import type { MontessoriFocus } from '@/lib/montessori';
import { SITE_UI_2026 } from '@/lib/site-config';
import HomePage2026 from './home-page-2026';

const AGE_BUCKETS = ['0-1', '1-2', '3-4', '5-6', '7-8', '9-10'];
const SUGGESTED_HASHTAGS = [
  '#animals', '#adventure', '#fantasy', '#space', '#underwater',
  '#dinosaurs', '#princess', '#superhero', '#nature', '#vehicles',
  '#magic', '#forest', '#ocean', '#castle', '#robot'
];

const PRICING_PACKAGES = [
  {
    type: 'credits10',
    name: 'Starter',
    credits: 10,
    price: '$15',
    pricePerBook: '$1.50',
    features: ['10 credits ($1.50/credit)', '10 coloring books or', '3 audio books', 'No watermarks', 'Forever access'],
  },
  {
    type: 'credits25',
    name: 'Family',
    credits: 25,
    price: '$30',
    pricePerBook: '$1.20',
    features: ['25 credits ($1.20/credit)', '25 coloring books or', '8 audio books', 'No watermarks', 'Forever access'],
    featured: true,
  },
  {
    type: 'credits60',
    name: 'Pro',
    credits: 60,
    price: '$60',
    pricePerBook: '$1.00',
    features: ['60 credits ($1.00/credit)', '60 coloring books or', '20 audio books', 'No watermarks', 'Forever access'],
  },
];

const SUBSCRIPTIONS = [
  {
    id: 'amaya_plus',
    name: 'Amaya Plus',
    price: '$19.99/mo',
    credits: 30,
    features: ['30 credits every month', '100+ Audio Story Gallery', 'Priority support', 'Cancel anytime'],
    description: '~10 audio books/month',
  },
  {
    id: 'amaya_pro',
    name: 'Amaya Pro',
    price: '$49.99/mo',
    credits: 100,
    features: ['100 credits every month', 'Everything in Plus', 'Priority generation (2× faster)', 'Voice selection', 'Early access to features'],
    featured: true,
    description: '~33 audio books/month',
  },
];

function HomePageContent() {
  const { data: session } = useSession();
  const searchParams = useSearchParams();
  const router = useRouter();
  const [prompt, setPrompt] = useState('');
  const [ageBucket, setAgeBucket] = useState<string | undefined>();
  const [selectedHashtags, setSelectedHashtags] = useState<string[]>([]);
  const [generating, setGenerating] = useState(false);
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [status, setStatus] = useState<any>(null);
  const [credits, setCredits] = useState(0);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string>('NONE');
  const [loading, setLoading] = useState<string | null>(null);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('Preparing your magical story...');
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [hasScrolledToResults, setHasScrolledToResults] = useState(false);
  const [showPurchaseSuccess, setShowPurchaseSuccess] = useState(false);
  const [generatingIdea, setGeneratingIdea] = useState(false);
  const [purchaseDetails, setPurchaseDetails] = useState<{type: 'credits' | 'subscription'; amount: number} | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [showMagicalStoryModal, setShowMagicalStoryModal] = useState(false);
  const [generatedStoryIdea, setGeneratedStoryIdea] = useState('');
  const [montessoriMode, setMontessoriMode] = useState(false);
  const [montessoriFocus, setMontessoriFocus] = useState<MontessoriFocus>('mixed');
  const [showMontessoriUpsell, setShowMontessoriUpsell] = useState(false);
  const [isSubscriber, setIsSubscriber] = useState(false);

  useEffect(() => {
    if (session?.user) {
      fetchUserCredits();
      
      // Check for pending purchase after login
      const pendingPurchase = sessionStorage.getItem('pendingPurchase');
      if (pendingPurchase) {
        sessionStorage.removeItem('pendingPurchase');
        const purchaseData = JSON.parse(pendingPurchase);
        
        if (purchaseData.type === 'subscription') {
          proceedToSubscription(purchaseData.plan || 'amaya_plus');
        } else {
          proceedToPurchase(purchaseData.packageType);
        }
      }
    }
  }, [session]);

  // Check for purchase success from URL params
  useEffect(() => {
    const purchase = searchParams.get('purchase');
    const type = searchParams.get('type');
    const amount = searchParams.get('amount');

    if (purchase === 'success' && type && amount) {
      setPurchaseDetails({
        type: type as 'credits' | 'subscription',
        amount: parseInt(amount, 10)
      });
      setShowPurchaseSuccess(true);
      
      // Refresh credits after purchase
      if (session?.user) {
        fetchUserCredits();
      }
      
      // Clean up URL
      router.replace('/', { scroll: false });
    }
  }, [searchParams, session, router]);

  useEffect(() => {
    if (generationId) {
      let pollInterval = 3000; // Start with 3 seconds
      let pollCount = 0;
      
      const poll = () => {
        checkStatus();
        pollCount++;
        
        // Adaptive polling: slow down over time
        if (pollCount > 20) { // After 1 minute (20 * 3s)
          pollInterval = 5000; // Slow to 5 seconds
        }
        if (pollCount > 60) { // After 3 minutes
          pollInterval = 8000; // Slow to 8 seconds
        }
      };
      
      // Initial check
      poll();
      
      // Set up interval with adaptive timing
      let timeoutId: NodeJS.Timeout;
      const scheduleNext = () => {
        timeoutId = setTimeout(() => {
          poll();
          scheduleNext();
        }, pollInterval);
      };
      
      scheduleNext();
      
      return () => clearTimeout(timeoutId);
    }
  }, [generationId]);

  // Realistic progress simulation based on actual generation time
  useEffect(() => {
    if (generating && !status?.pdfUrl) {
      setGenerationProgress(0);
      setGenerationStep('⚡ Generating all pages in parallel...');
      setPreviewImages([]);

      const startTime = Date.now();
      // Estimate: 2.5 min for demo (3 pages), 9 min for full (13 pages)
      const estimatedDuration = status?.pages === 13 ? 540000 : 150000; // in milliseconds
      
      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const timeBasedProgress = (elapsed / estimatedDuration) * 100;
        
        setGenerationProgress((prev) => {
          // Blend time-based progress with gradual increments
          // Never exceed 92% until actual completion
          const targetProgress = Math.min(timeBasedProgress, 92);
          const increment = (targetProgress - prev) * 0.15; // Smooth acceleration
          const newProgress = Math.min(prev + increment + Math.random() * 0.5, 92);
          
          // Update step messages based on progress
          if (newProgress >= 5 && newProgress < 25) {
            setGenerationStep('🎨 Creating all coloring pages at once...');
          } else if (newProgress >= 25 && newProgress < 50) {
            setGenerationStep('✨ Adding magical details to each page...');
          } else if (newProgress >= 50 && newProgress < 70) {
            setGenerationStep('🖍️ Perfecting coloring-friendly lines...');
          } else if (newProgress >= 70 && newProgress < 85) {
            setGenerationStep('📄 Assembling your PDF...');
          } else if (newProgress >= 85) {
            setGenerationStep('🎉 Almost ready!');
          }
          
          return newProgress;
        });
      }, 800); // Smoother, more realistic updates

      return () => clearInterval(progressInterval);
    }
  }, [generating, status?.pdfUrl, status?.pages]);

  // Update progress when generation completes
  useEffect(() => {
    if (status?.status === 'completed') {
      setGenerationProgress(100);
      setGenerationStep('🎉 Your magical book is ready!');
      
      if (status.previewUrls) {
        setPreviewImages(status.previewUrls);
      }

      // Close modal and scroll to results (only once per generation)
      setTimeout(() => {
        setGenerating(false);
        
        // Scroll to the results section after modal closes (only if not scrolled yet)
        if (!hasScrolledToResults) {
          setTimeout(() => {
            const resultsSection = document.getElementById('book-results');
            if (resultsSection) {
              // Force scroll to the results
              resultsSection.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'nearest'
              });
              setHasScrolledToResults(true);
            }
          }, 200);
        }
      }, 800);
    } else if (status?.status === 'failed') {
      setGenerationProgress(0);
      setGenerationStep('Something went wrong. Please try again.');
      setTimeout(() => {
        setGenerating(false);
      }, 2000);
    }
  }, [status, hasScrolledToResults]);

  async function fetchUserCredits() {
    try {
      const res = await fetch('/api/account');
      if (res.ok) {
        const data = await res.json();
        setCredits(data.credits || 0);
        setSubscriptionPlan(data.subscriptionPlan || 'NONE');
        setIsSubscriber(data.isSubscriber || false);
      }
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    }
  }

  async function checkStatus() {
    if (!generationId) return;
    
    try {
      const res = await fetch(`/api/generate/status?id=${generationId}`);
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        
        if (data.status === 'completed' || data.status === 'failed') {
          setGenerating(false);
        }
      }
    } catch (error) {
      console.error('Status check failed:', error);
    }
  }

  // Generate AI story idea
  async function handleGenerateIdea() {
    setGeneratingIdea(true);
    try {
      const res = await fetch('/api/generate-idea', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await res.json();

      if (res.ok && data.idea) {
        setPrompt(data.idea);
      } else {
        alert('Failed to generate idea. Please try again.');
      }
    } catch (error) {
      console.error('Idea generation error:', error);
      alert('Failed to generate idea. Please try again.');
    } finally {
      setGeneratingIdea(false);
    }
  }

  // Smart generation router - chooses between preview and full based on user state
  async function handleGenerate() {
    // If prompt is empty, auto-generate a magical story idea
    if (!prompt.trim()) {
      await generateAndShowMagicalStory();
      return;
    }

    // Not logged in → Generate preview (rate limited by IP)
    if (!session) {
      return handleGeneratePreview();
    }
    
    // Logged in with credits or subscriber → Generate FULL book
    if (credits > 0 || session.user?.isSubscriber) {
      return handleGenerateFull();
    }
    
    // Logged in with 0 credits and not subscriber → Show purchase modal
    setShowInsufficientCreditsModal(true);
  }

  // Auto-generate magical story when field is empty
  async function generateAndShowMagicalStory() {
    setGeneratingIdea(true);
    try {
      const res = await fetch('/api/generate-idea', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await res.json();

      if (res.ok && data.idea) {
        setGeneratedStoryIdea(data.idea);
        setShowMagicalStoryModal(true);
      } else {
        alert('Failed to generate a magical story. Please try again!');
      }
    } catch (error) {
      console.error('Magical story generation error:', error);
      alert('Failed to generate a magical story. Please try again!');
    } finally {
      setGeneratingIdea(false);
    }
  }

  // Handle user proceeding with generated story
  async function handleProceedWithGeneratedStory() {
    // Store the idea in a variable to avoid state update timing issues
    const storyToGenerate = generatedStoryIdea;
    setPrompt(storyToGenerate);
    setShowMagicalStoryModal(false);
    
    // Small delay to let user see the prompt filled in
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Direct routing logic - generate immediately without checking prompt again
    // Not logged in → Generate preview (rate limited by IP)
    if (!session) {
      await generateWithPrompt(storyToGenerate, false);
      return;
    }
    
    // Logged in with credits or subscriber → Generate FULL book
    if (credits > 0 || session.user?.isSubscriber) {
      await generateWithPrompt(storyToGenerate, true);
      return;
    }
    
    // Logged in with 0 credits and not subscriber → Show purchase modal
    setShowInsufficientCreditsModal(true);
  }

  // Handle user canceling and wanting to write their own
  function handleCancelGeneratedStory() {
    setShowMagicalStoryModal(false);
    setGeneratedStoryIdea('');
  }

  // Helper function to generate with a specific prompt (avoids state timing issues)
  async function generateWithPrompt(promptText: string, isFullBook: boolean) {
    if (!promptText.trim()) {
      return;
    }

    setGenerating(true);
    setStatus(null);
    setHasScrolledToResults(false);

    try {
      const endpoint = isFullBook ? '/api/generate/full' : '/api/generate/preview';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          prompt: promptText, 
          ageBucket, 
          hashtags: selectedHashtags,
          montessoriMode,
          montessoriFocus
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        setGenerating(false);
        
        if (error.error === 'rate_limit') {
          setIsRateLimited(true);
          setShowRateLimitModal(true);
        } else if (error.error && error.error.toLowerCase().includes('insufficient credits')) {
          setShowInsufficientCreditsModal(true);
        } else if (error.error === 'no_credits') {
          setShowInsufficientCreditsModal(true);
        } else {
          alert(error.message || error.error || 'Failed to generate book');
        }
        return;
      }

      const data = await res.json();
      setGenerationId(data.generationId);
      setIsRateLimited(false);
      
      if (isFullBook) {
        fetchUserCredits();
      }
    } catch (error) {
      console.error('Generation failed:', error);
      alert('Failed to generate book');
      setGenerating(false);
    }
  }

  async function handleGeneratePreview() {
    if (!prompt.trim()) {
      return;
    }

    setGenerating(true);
    setStatus(null);
    setHasScrolledToResults(false); // Reset scroll flag for new generation

    try {
      const res = await fetch('/api/generate/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          prompt, 
          ageBucket, 
          hashtags: selectedHashtags,
          montessoriMode,
          montessoriFocus
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        setGenerating(false);
        
        // Handle different error types
        if (error.error === 'rate_limit') {
          setIsRateLimited(true);
          setShowRateLimitModal(true);
        } else if (error.error === 'no_credits') {
          setShowInsufficientCreditsModal(true);
        } else {
          alert(error.message || error.error || 'Failed to generate preview');
        }
        return;
      }

      const data = await res.json();
      setGenerationId(data.generationId);
      setIsRateLimited(false); // Reset rate limit state on successful generation
    } catch (error) {
      console.error('Preview generation failed:', error);
      alert('Failed to generate preview');
      setGenerating(false);
    }
  }

  async function handleGenerateFull() {
    if (!session) {
      alert('Please sign in to generate a full book');
      return;
    }

    if (!prompt.trim()) {
      return;
    }

    setGenerating(true);
    setStatus(null);
    setHasScrolledToResults(false); // Reset scroll flag for new generation

    try {
      const res = await fetch('/api/generate/full', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          prompt, 
          ageBucket, 
          hashtags: selectedHashtags,
          montessoriMode,
          montessoriFocus
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        
        // Check if error is about insufficient credits
        if (error.error && error.error.toLowerCase().includes('insufficient credits')) {
          setGenerating(false);
          setShowInsufficientCreditsModal(true);
          return;
        }
        
        alert(error.error || 'Failed to generate full book');
        setGenerating(false);
        return;
      }

      const data = await res.json();
      setGenerationId(data.generationId);
      fetchUserCredits();
    } catch (error) {
      console.error('Full book generation failed:', error);
      alert('Failed to generate full book');
      setGenerating(false);
    }
  }

  function toggleHashtag(tag: string) {
    setSelectedHashtags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  }

  async function proceedToPurchase(packageType: string) {
    setLoading(packageType);

    try {
      const res = await fetch('/api/checkout/credits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packageType }),
      });

      if (!res.ok) {
        throw new Error('Checkout failed');
      }

      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to start checkout. Please try again.');
      setLoading(null);
    }
  }

  async function proceedToSubscription(plan: 'amaya_plus' | 'amaya_pro' = 'amaya_plus') {
    setLoading(plan);

    try {
      const res = await fetch('/api/checkout/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan }),
      });

      if (!res.ok) {
        throw new Error('Subscription failed');
      }

      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Subscribe error:', error);
      alert('Failed to start subscription. Please try again.');
      setLoading(null);
    }
  }

  async function handlePurchase(packageType: string) {
    if (!session) {
      // Store purchase intent and redirect to login
      sessionStorage.setItem('pendingPurchase', JSON.stringify({ 
        type: 'credits',
        packageType 
      }));
      // Redirect to login page with callback
      const callbackUrl = encodeURIComponent(window.location.href);
      router.push(`/login?callbackUrl=${callbackUrl}`);
      return;
    }

    proceedToPurchase(packageType);
  }

  async function handleSubscribe(plan: 'amaya_plus' | 'amaya_pro' = 'amaya_plus') {
    if (!session) {
      // Store subscription intent and redirect to login
      sessionStorage.setItem('pendingPurchase', JSON.stringify({ 
        type: 'subscription',
        plan
      }));
      // Redirect to login page with callback
      const callbackUrl = encodeURIComponent(window.location.href);
      router.push(`/login?callbackUrl=${callbackUrl}`);
      return;
    }

    proceedToSubscription(plan);
  }

  function scrollToCreate() {
    document.getElementById('create-section')?.scrollIntoView({ behavior: 'smooth' });
  }

  return (
    <>
      {/* Magical Generation Modal */}
      <MagicalGenerationModal
        isGenerating={generating}
        progress={generationProgress}
        currentStep={generationStep}
        previewImages={previewImages}
      />

      {/* Magical Story Modal */}
      <MagicalStoryModal
        isOpen={showMagicalStoryModal}
        storyIdea={generatedStoryIdea}
        onProceed={handleProceedWithGeneratedStory}
        onCancel={handleCancelGeneratedStory}
      />

      {/* Rate Limit Modal */}
      <RateLimitModal
        isOpen={showRateLimitModal}
        onClose={() => setShowRateLimitModal(false)}
        isLoggedIn={!!session}
      />

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        onPurchase={(packageType) => {
          setShowInsufficientCreditsModal(false);
          handlePurchase(packageType);
        }}
        onSubscribe={(plan) => {
          setShowInsufficientCreditsModal(false);
          handleSubscribe(plan || 'amaya_plus');
        }}
      />

      {/* Purchase Success Modal */}
      {purchaseDetails && (
        <PurchaseSuccessModal
          isOpen={showPurchaseSuccess}
          onClose={() => {
            setShowPurchaseSuccess(false);
            setPurchaseDetails(null);
          }}
          purchaseType={purchaseDetails.type}
          amount={purchaseDetails.amount}
        />
      )}

      <div style={{ minHeight: '100vh', background: 'var(--color-bg-secondary)', transform: 'scale(0.9)', transformOrigin: 'top center', width: '111.11%', marginLeft: '-5.56%' }}>
        
        {/* 2026 Award-Winning Hero Headline */}
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: '3rem 1.5rem 1.5rem',
          position: 'relative'
        }}>
          <style jsx>{`
            @keyframes gradientFlow {
              0%, 100% {
                background-position: 0% 50%;
              }
              50% {
                background-position: 100% 50%;
              }
            }

            @keyframes floatUp {
              0%, 100% {
                transform: translateY(0) rotate(0deg);
                opacity: 0.7;
              }
              50% {
                transform: translateY(-15px) rotate(5deg);
                opacity: 1;
              }
            }

            @keyframes sparkleFloat {
              0%, 100% {
                transform: translate(0, 0) scale(1) rotate(0deg);
                opacity: 0;
              }
              50% {
                opacity: 1;
              }
              100% {
                transform: translate(var(--tx), var(--ty)) scale(0) rotate(360deg);
                opacity: 0;
              }
            }

            @keyframes pulseGently {
              0%, 100% {
                transform: scale(1);
                filter: blur(0px);
              }
              50% {
                transform: scale(1.05);
                filter: blur(1px);
              }
            }

            @keyframes shimmerSweep {
              0% {
                transform: translateX(-100%) skewX(-15deg);
              }
              100% {
                transform: translateX(200%) skewX(-15deg);
              }
            }

            .hero-headline-2026 {
              position: relative;
              text-align: center;
              padding: 2rem 1rem;
              isolation: isolate;
            }

            .headline-main {
              font-size: clamp(2rem, 7vw, 4rem);
              font-weight: 900;
              line-height: 1.1;
              margin: 0;
              padding: 0.5rem 0.5rem 1.5rem;
              position: relative;
              display: inline-block;
              background: linear-gradient(
                135deg,
                #A8D8E6 0%,
                #C9B6E4 20%,
                #F8C8A0 40%,
                #A8D8E6 60%,
                #C9B6E4 80%,
                #F8C8A0 100%
              );
              background-size: 300% 300%;
              background-clip: text;
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              animation: gradientFlow 8s ease infinite;
              filter: drop-shadow(0 4px 12px rgba(168, 216, 230, 0.4))
                      drop-shadow(0 2px 8px rgba(201, 182, 228, 0.3));
              transition: transform 0.3s ease;
            }

            .headline-main:hover {
              transform: scale(1.02);
              filter: drop-shadow(0 6px 16px rgba(168, 216, 230, 0.6))
                      drop-shadow(0 4px 12px rgba(201, 182, 228, 0.5));
            }

            .headline-glow {
              position: absolute;
              inset: -20px;
              background: radial-gradient(
                ellipse at center,
                rgba(168, 216, 230, 0.15) 0%,
                rgba(201, 182, 228, 0.1) 40%,
                transparent 70%
              );
              animation: pulseGently 4s ease-in-out infinite;
              z-index: -1;
              pointer-events: none;
            }

            .floating-icon {
              position: absolute;
              font-size: 2rem;
              animation: floatUp 3s ease-in-out infinite;
              filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
            }

            .floating-icon:nth-child(1) {
              top: 10%;
              left: 5%;
              animation-delay: 0s;
            }

            .floating-icon:nth-child(2) {
              top: 20%;
              right: 8%;
              animation-delay: 1s;
            }

            .floating-icon:nth-child(3) {
              bottom: 15%;
              left: 10%;
              animation-delay: 0.5s;
            }

            .floating-icon:nth-child(4) {
              bottom: 20%;
              right: 5%;
              animation-delay: 1.5s;
            }

            .sparkle-particle {
              position: absolute;
              width: 8px;
              height: 8px;
              background: radial-gradient(circle, 
                rgba(248, 200, 160, 1) 0%, 
                rgba(201, 182, 228, 0.8) 50%,
                rgba(168, 216, 230, 0.4) 100%
              );
              border-radius: 50%;
              animation: sparkleFloat 4s ease-in-out infinite;
              pointer-events: none;
            }

            .sparkle-particle:nth-child(1) { --tx: -100px; --ty: -80px; animation-delay: 0s; top: 50%; left: 20%; }
            .sparkle-particle:nth-child(2) { --tx: 120px; --ty: -100px; animation-delay: 1s; top: 40%; right: 15%; }
            .sparkle-particle:nth-child(3) { --tx: -80px; --ty: 90px; animation-delay: 2s; bottom: 30%; left: 25%; }
            .sparkle-particle:nth-child(4) { --tx: 100px; --ty: 80px; animation-delay: 3s; bottom: 40%; right: 20%; }
            .sparkle-particle:nth-child(5) { --tx: 0px; --ty: -120px; animation-delay: 1.5s; top: 60%; left: 50%; }
            .sparkle-particle:nth-child(6) { --tx: -90px; --ty: -70px; animation-delay: 2.5s; top: 35%; left: 35%; }

            .headline-shimmer {
              position: absolute;
              inset: 0;
              background: linear-gradient(
                90deg,
                transparent 0%,
                rgba(255, 255, 255, 0.6) 50%,
                transparent 100%
              );
              width: 50%;
              height: 100%;
              animation: shimmerSweep 3s ease-in-out infinite;
              pointer-events: none;
            }

            @media (max-width: 768px) {
              .headline-main {
                font-size: clamp(1.75rem, 8vw, 3rem);
                padding: 0.5rem 0.5rem 1.25rem;
              }

              .floating-icon {
                font-size: 1.5rem;
              }

              .hero-headline-2026 {
                padding: 1.5rem 0.5rem;
              }
            }
          `}</style>

          <div className="hero-headline-2026">
            {/* Floating Icons */}
            <div className="floating-icon">✨</div>
            <div className="floating-icon">🎨</div>
            <div className="floating-icon">📚</div>
            <div className="floating-icon">🌟</div>

            {/* Sparkle Particles */}
            <div className="sparkle-particle"></div>
            <div className="sparkle-particle"></div>
            <div className="sparkle-particle"></div>
            <div className="sparkle-particle"></div>
            <div className="sparkle-particle"></div>
            <div className="sparkle-particle"></div>

            {/* Main Headline */}
            <h1 className="headline-main">
              <span className="headline-glow"></span>
              <span className="headline-shimmer"></span>
              Bring Your Child's Imagination to Life
            </h1>

            {/* Subheadline */}
            <p style={{
              fontSize: 'clamp(1rem, 2vw, 1.25rem)',
              color: '#5A5A5A',
              maxWidth: '800px',
              margin: '0 auto 3rem',
              lineHeight: '1.6',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              padding: '0 1.5rem'
            }}>
              Create Montessori-inspired AI coloring books &amp; audio stories in minutes.
            </p>
          </div>
        </div>

        {/* Video Hero Section */}
      <div style={{ 
        maxWidth: '900px', 
        margin: '0 auto',
        padding: '0 1.5rem 2rem',
        transform: 'scale(1.111)',
        transformOrigin: 'center'
      }}>
        <AudioBookVideoPlayer 
          videoSrc={session 
            ? "https://pub-c66b80290e37412f86676f07b6cbcd28.r2.dev/signin.mp4"
            : "https://pub-c66b80290e37412f86676f07b6cbcd28.r2.dev/amaya-stories-ai-book-generation.mp4"
          }
          posterSrc="https://pub-c66b80290e37412f86676f07b6cbcd28.r2.dev/Amaya%20Story.webp"
        />

        {/* CTA Button under video - Only for non-signed in users */}
        {!session && (
          <div style={{ textAlign: 'center', marginBottom: '1rem', position: 'relative' }}>
          <style jsx>{`
            @keyframes magicShimmer {
              0% {
                background-position: -200% center;
              }
              100% {
                background-position: 200% center;
              }
            }

            @keyframes floatSparkle {
              0%, 100% {
                transform: translateY(0) rotate(0deg);
                opacity: 1;
              }
              50% {
                transform: translateY(-10px) rotate(180deg);
                opacity: 0.8;
              }
            }

            @keyframes pulseGlow {
              0%, 100% {
                box-shadow: 0 0 20px rgba(168, 216, 230, 0.4),
                            0 0 40px rgba(201, 182, 228, 0.3),
                            0 4px 30px rgba(168, 216, 230, 0.5),
                            inset 0 0 20px rgba(255, 255, 255, 0.1);
              }
              50% {
                box-shadow: 0 0 30px rgba(168, 216, 230, 0.6),
                            0 0 60px rgba(201, 182, 228, 0.5),
                            0 4px 40px rgba(168, 216, 230, 0.7),
                            inset 0 0 30px rgba(255, 255, 255, 0.2);
              }
            }

            @keyframes particleFloat {
              0% {
                transform: translate(0, 0) scale(0);
                opacity: 0;
              }
              50% {
                opacity: 1;
              }
              100% {
                transform: translate(var(--tx), var(--ty)) scale(1);
                opacity: 0;
              }
            }

            .magic-btn {
              position: relative;
              font-size: 1.5rem;
              font-weight: 700;
              padding: 1.5rem 4rem;
              background: linear-gradient(
                135deg,
                #A8D8E6 0%,
                #C9B6E4 25%,
                #F8C8A0 50%,
                #C9B6E4 75%,
                #A8D8E6 100%
              );
              background-size: 200% 200%;
              border: none;
              border-radius: 100px;
              color: white;
              cursor: pointer;
              display: inline-flex;
              align-items: center;
              gap: 1rem;
              transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
              animation: magicShimmer 3s linear infinite, pulseGlow 2s ease-in-out infinite;
              overflow: hidden;
              text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
              isolation: isolate;
            }

            .magic-btn::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: linear-gradient(
                135deg,
                rgba(255, 255, 255, 0.3) 0%,
                rgba(255, 255, 255, 0) 50%,
                rgba(255, 255, 255, 0.3) 100%
              );
              background-size: 200% 200%;
              animation: magicShimmer 2s linear infinite;
              border-radius: 100px;
              z-index: 0;
              pointer-events: none;
            }

            .magic-btn:hover {
              transform: translateY(-4px) scale(1.05);
              animation: magicShimmer 1.5s linear infinite, pulseGlow 1s ease-in-out infinite;
            }

            .magic-btn:active {
              transform: translateY(-2px) scale(1.02);
            }

            .magic-btn-content {
              position: relative;
              z-index: 1;
              display: inline-flex;
              align-items: center;
              gap: 1rem;
            }

            .sparkle-icon {
              animation: floatSparkle 2s ease-in-out infinite;
              filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.8));
              position: relative;
              z-index: 1;
            }

            .magic-particles {
              position: absolute;
              top: 50%;
              left: 50%;
              width: 100%;
              height: 100%;
              pointer-events: none;
            }

            .particle {
              position: absolute;
              width: 6px;
              height: 6px;
              background: radial-gradient(circle, rgba(255, 255, 255, 1) 0%, rgba(168, 216, 230, 0.8) 100%);
              border-radius: 50%;
              animation: particleFloat 2s ease-out infinite;
            }

            .particle:nth-child(1) { --tx: -80px; --ty: -80px; animation-delay: 0s; left: 20%; }
            .particle:nth-child(2) { --tx: 80px; --ty: -70px; animation-delay: 0.3s; left: 80%; }
            .particle:nth-child(3) { --tx: -60px; --ty: 60px; animation-delay: 0.6s; left: 30%; }
            .particle:nth-child(4) { --tx: 90px; --ty: 80px; animation-delay: 0.9s; left: 70%; }
            .particle:nth-child(5) { --tx: 0px; --ty: -100px; animation-delay: 1.2s; left: 50%; }
            .particle:nth-child(6) { --tx: -70px; --ty: -60px; animation-delay: 1.5s; left: 40%; }
          `}</style>
          
          <button
            onClick={scrollToCreate}
            className="magic-btn"
          >
            <div className="magic-particles">
              <div className="particle"></div>
              <div className="particle"></div>
              <div className="particle"></div>
              <div className="particle"></div>
              <div className="particle"></div>
              <div className="particle"></div>
            </div>
            <span className="magic-btn-content">
              <Sparkles size={32} className="sparkle-icon" />
              <span>Start Creating</span>
            </span>
          </button>
          {credits === 0 && (
            <p style={{ 
              marginTop: '1.5rem', 
              color: 'var(--color-text-secondary)',
              fontSize: '1.125rem',
              fontWeight: 500
            }}>
              ✨ Try it free • No credit card required
            </p>
          )}
        </div>
        )}
      </div>

      {/* Create Section */}
      <div id="create-section" className="container" style={{ 
        maxWidth: '900px', 
        margin: '0 auto', 
        padding: '1rem 1.5rem 3rem',
        scrollMarginTop: '80px'
      }}>
        {/* Hide "Turn your idea into a magical coloring book" section for signed-in users */}
        {!session && (
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h2 style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', marginBottom: '0.75rem' }}>
              Turn your idea into a magical coloring book.
            </h2>
          </div>
        )}

        <div className="card">
          <div className="card-header-mobile" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div style={{ flex: 1 }}>
              <h3 className="card-title-mobile" style={{ fontSize: '1.5rem', margin: 0, marginBottom: '0.5rem' }}>🌟 What Shall We Create Today?</h3>
              <p style={{ 
                fontSize: '0.875rem', 
                color: 'var(--color-text-secondary)', 
                margin: 0,
                fontWeight: 500
              }}>
                ✨ 1 credit per coloring book
              </p>
            </div>
            <button
              onClick={handleGenerateIdea}
              disabled={generatingIdea || generating}
              className="ai-idea-btn"
              title="Generate AI story idea"
            >
              {generatingIdea ? (
                <>
                  <Loader2 className="spinning" size={16} />
                  <span>Thinking...</span>
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  <span>Inspire Me</span>
                </>
              )}
            </button>
          </div>
          <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
            <textarea
              placeholder="Describe your story idea... (e.g., 'A friendly dragon who loves to bake cookies and share them with forest animals')"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={generating}
              style={{ marginBottom: '0.5rem' }}
            />
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              fontSize: '0.875rem',
              color: 'var(--color-text-secondary)'
            }}>
              <span style={{ fontWeight: 500 }}>
                {prompt.trim().length > 0 ? '✅ Ready to create!' : '✨ Describe your idea or click Generate for magic!'}
              </span>
            </div>
          </div>

          <h4 style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>Age Range (optional)</h4>
          <div className="age-chips">
            {AGE_BUCKETS.map((age) => (
              <button
                key={age}
                className={`age-chip ${ageBucket === age ? 'active' : ''}`}
                onClick={() => setAgeBucket(ageBucket === age ? undefined : age)}
                disabled={generating}
              >
                {age} years
              </button>
            ))}
          </div>

          <h4 style={{ fontSize: '1.125rem', margin: '1.5rem 0 0.75rem' }}>Themes (optional)</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '2rem' }}>
            {SUGGESTED_HASHTAGS.map((tag) => (
              <button
                key={tag}
                className={`hashtag-chip ${selectedHashtags.includes(tag) ? 'age-chip active' : ''}`}
                onClick={() => toggleHashtag(tag)}
                disabled={generating}
              >
                {tag}
              </button>
            ))}
          </div>

          {/* Montessori Mode Section */}
          <div style={{ marginBottom: '2rem' }}>
            <MontessoriToggle
              enabled={montessoriMode}
              isSubscriber={isSubscriber}
              onChange={setMontessoriMode}
              onUpsell={() => setShowMontessoriUpsell(true)}
            />
            
            {montessoriMode && (
              <div style={{ marginTop: '1rem' }}>
                <MontessoriFocusSelector
                  value={montessoriFocus}
                  onChange={setMontessoriFocus}
                  disabled={generating}
                />
              </div>
            )}
          </div>

          {/* Montessori Upsell Modal */}
          <MontessoriUpsellModal
            isOpen={showMontessoriUpsell}
            onClose={() => setShowMontessoriUpsell(false)}
          />

          <style jsx>{`
            .beautiful-btn {
              position: relative;
              font-size: 1.25rem;
              font-weight: 700;
              padding: 1rem 2rem;
              border: none;
              border-radius: 60px;
              cursor: pointer;
              display: inline-flex;
              align-items: center;
              gap: 0.75rem;
              transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              overflow: visible;
              white-space: nowrap;
              justify-content: center;
              isolation: isolate;
            }

            .beautiful-btn::before {
              content: '';
              position: absolute;
              inset: 0;
              border-radius: 60px;
              background: rgba(255, 255, 255, 0);
              transition: background 0.3s;
              z-index: -1;
            }

            .beautiful-btn:hover::before {
              background: rgba(255, 255, 255, 0.15);
            }

            .preview-btn {
              background: linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 100%);
              color: white;
              box-shadow: 0 4px 20px rgba(168, 216, 230, 0.4),
                          0 0 30px rgba(201, 182, 228, 0.2);
            }

            .preview-btn:hover:not(:disabled) {
              transform: translateY(-2px);
              box-shadow: 0 6px 30px rgba(168, 216, 230, 0.6),
                          0 0 40px rgba(201, 182, 228, 0.3);
            }

            .beautiful-btn:disabled {
              opacity: 0.6;
              cursor: not-allowed;
              transform: none;
            }

            .beautiful-btn:active:not(:disabled) {
              transform: translateY(0);
            }

            .btn-content {
              display: inline-flex;
              align-items: center;
              gap: 0.75rem;
              position: relative;
              z-index: 1;
            }

            .btn-icon {
              flex-shrink: 0;
            }

            .ai-idea-btn {
              display: inline-flex;
              align-items: center;
              gap: 0.5rem;
              padding: 0.5rem 1rem;
              background: linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%);
              color: white;
              border: none;
              border-radius: 50px;
              cursor: pointer;
              font-size: 0.875rem;
              font-weight: 600;
              transition: all 0.3s ease;
              box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3);
            }

            .ai-idea-btn:hover:not(:disabled) {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(139, 92, 246, 0.5);
              background: linear-gradient(135deg, #7C3AED 0%, #8B5CF6 100%);
            }

            .ai-idea-btn:disabled {
              opacity: 0.6;
              cursor: not-allowed;
              transform: none;
            }

            .ai-idea-btn .spinning {
              animation: spin 1s linear infinite;
            }

            @keyframes spin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }

            @keyframes pulse {
              0%, 100% {
                box-shadow: 0 8px 24px rgba(255, 215, 0, 0.4), 0 0 40px rgba(255, 165, 0, 0.2);
                transform: scale(1);
              }
              50% {
                box-shadow: 0 12px 32px rgba(255, 215, 0, 0.6), 0 0 60px rgba(255, 165, 0, 0.4);
                transform: scale(1.02);
              }
            }

            /* Mobile Responsive Styles */
            @media (max-width: 768px) {
              .card-header-mobile {
                flex-direction: column;
                align-items: stretch !important;
                gap: 1rem !important;
              }

              .card-title-mobile {
                font-size: 1.25rem !important;
                text-align: center;
              }

              .ai-idea-btn {
                width: 100%;
                justify-content: center;
                padding: 0.75rem 1.5rem !important;
                font-size: 1rem !important;
              }

              .beautiful-btn {
                width: 100% !important;
                min-width: 100% !important;
                padding: 1rem 1.5rem !important;
                font-size: 1.125rem !important;
              }

              .age-chips {
                justify-content: center;
              }

              .age-chip {
                flex: 0 1 calc(50% - 0.5rem);
                min-width: 120px;
              }

              .unlimited-badge-mobile {
                padding: 0.875rem 1.5rem !important;
                gap: 0.5rem !important;
                font-size: 1rem !important;
                max-width: 95vw;
                text-align: center;
              }

              .badge-text-mobile {
                font-size: 0.9375rem !important;
                white-space: nowrap;
              }

              .credits-badge-mobile {
                padding: 0.625rem 1.25rem !important;
                font-size: 0.9375rem !important;
              }
            }

            @media (max-width: 480px) {
              .card-title-mobile {
                font-size: 1.125rem !important;
              }

              .ai-idea-btn {
                font-size: 0.9375rem !important;
              }

              .beautiful-btn {
                padding: 0.875rem 1.25rem !important;
                font-size: 1rem !important;
              }

              .age-chip {
                flex: 1 1 100%;
                max-width: 100%;
              }

              .unlimited-badge-mobile {
                padding: 0.75rem 1rem !important;
                font-size: 0.875rem !important;
                gap: 0.375rem !important;
                flex-wrap: wrap;
                justify-content: center;
              }

              .badge-icon-mobile {
                width: 20px !important;
                height: 20px !important;
              }

              .badge-text-mobile {
                font-size: 0.875rem !important;
                flex: 0 1 auto;
              }

              .credits-badge-mobile {
                padding: 0.5rem 1rem !important;
                font-size: 0.875rem !important;
              }
            }
          `}</style>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
            {/* Credits Badge - Only show for non-subscribers with credits */}
            {!session?.user?.isSubscriber && session && credits > 0 && (
              <div className="credits-badge-mobile" style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, rgba(168, 216, 230, 0.1) 0%, rgba(201, 182, 228, 0.1) 100%)',
                borderRadius: '50px',
                border: '2px solid var(--color-soft-blue)',
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--color-primary)',
                boxShadow: '0 4px 12px rgba(168, 216, 230, 0.2)'
              }}>
                <Sparkles size={20} style={{ color: 'var(--color-primary)' }} />
                <span>{credits} Credits Available</span>
              </div>
            )}
            
            <div style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
              <button
                className="beautiful-btn preview-btn"
                onClick={handleGenerate}
                disabled={generating || generatingIdea}
                style={{ minWidth: 'clamp(280px, 90vw, 380px)', padding: '1.25rem 2.5rem', maxWidth: '100%' }}
                title={!prompt.trim() ? 'Click to generate a magical story idea for you!' : 'Generate your coloring book'}
              >
                <span className="btn-content">
                  {generatingIdea ? (
                    <>
                      <Loader2 className="loading-spinner btn-icon" size={22} />
                      <span>Generating Story Idea...</span>
                    </>
                  ) : generating && !status?.pdfUrl ? (
                    <>
                      <Loader2 className="loading-spinner btn-icon" size={22} />
                      <span>Creating Your Magic Book...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles size={22} className="btn-icon" />
                      <span>
                        {!prompt.trim() 
                          ? 'Generate Story Idea ✨'
                          : session 
                            ? 'Create Your Coloring Book' 
                            : (isRateLimited ? 'Create Your Coloring Book' : 'Create Your Free Magic Book')}
                      </span>
                    </>
                  )}
                </span>
              </button>
            </div>
          </div>

          {!session && (
            <p style={{ marginTop: '1rem', fontSize: '0.9375rem', color: 'var(--color-text-secondary)', textAlign: 'center', fontWeight: 500 }}>
              ✨ Free 3-page demo • No credit card • Sign in to save & unlock more
            </p>
          )}
          {session?.user?.isSubscriber && (
            <p style={{ marginTop: '1rem', fontSize: '0.9375rem', color: 'var(--color-text-secondary)', textAlign: 'center', fontWeight: 500 }}>
              ⚡ Unlimited subscriber • Create as many books as you want!<br />
              <span style={{ fontSize: '0.875rem', opacity: 0.8 }}>⏱️ Generation takes approximately 2-3 minutes</span>
            </p>
          )}
          {!session?.user?.isSubscriber && session && credits > 0 && (
            <p style={{ marginTop: '1rem', fontSize: '0.9375rem', color: 'var(--color-text-secondary)', textAlign: 'center', fontWeight: 500 }}>
              🎨 Generate a full 13-page coloring book • 1 credit will be used<br />
              <span style={{ fontSize: '0.875rem', opacity: 0.8 }}>⏱️ Generation takes approximately 2-3 minutes</span>
            </p>
          )}
          {session && credits === 0 && !session.user?.isSubscriber && (
            <p style={{ marginTop: '1rem', fontSize: '0.9375rem', color: 'var(--color-text-secondary)', textAlign: 'center', fontWeight: 500 }}>
              💎 Purchase credits to create your full coloring book
            </p>
          )}
        </div>

        {status && (
          <div id="book-results" className="card" style={{ marginTop: '2rem', scrollMarginTop: '100px' }}>
            <h3 style={{ marginBottom: '1rem' }}>✨Ta-da! Amaya finished your storybook!</h3>
            
            {status.status === 'processing' && (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Loader2 className="loading-spinner" size={48} style={{ margin: '0 auto' }} />
                <p style={{ marginTop: '1rem' }}>
                  Creating your magical book... This may take a few minutes.
                </p>
              </div>
            )}

            {status.status === 'completed' && (
              <>
                {status.previewUrls && status.previewUrls.length > 0 && (
                  <>
                    {/* Cover Image (First Image - Gemini Generated) */}
                    <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
                      <div style={{ 
                        display: 'inline-block',
                        position: 'relative',
                        maxWidth: '500px',
                        width: '100%'
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: '-1rem',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: 'linear-gradient(135deg, var(--color-primary), var(--color-accent))',
                          color: 'white',
                          padding: '0.5rem 1.5rem',
                          borderRadius: 'var(--radius-full)',
                          fontSize: '0.875rem',
                          fontWeight: 700,
                          boxShadow: 'var(--shadow-lg)',
                          zIndex: 10
                        }}>
                          📖 Cover
                        </div>
                        <img 
                          src={status.previewUrls[0]} 
                          alt="Book Cover" 
                          className="book-preview-img"
                          style={{
                            width: '100%',
                            borderRadius: 'var(--radius-xl)',
                            boxShadow: '0 20px 40px -10px rgba(180, 160, 229, 0.4), 0 10px 20px -5px rgba(168, 216, 230, 0.3)',
                            border: '3px solid var(--color-border)',
                          }}
                        />
                      </div>
                    </div>

                    {/* Content Pages (Remaining Images) */}
                    {status.previewUrls.length > 1 && (
                      <>
                        <h4 style={{ 
                          fontSize: '1.125rem', 
                          fontWeight: 700, 
                          marginBottom: '1rem',
                          textAlign: 'center',
                          color: 'var(--color-text-primary)'
                        }}>
                          🎨 Coloring Pages
                        </h4>
                        <div className="image-grid">
                          {status.previewUrls.slice(1).map((url: string, i: number) => (
                            <div key={i + 1} className="image-card">
                              <img src={url} alt={`Page ${i + 2}`} />
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}

                {status.pdfUrl && (
                  <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                    <a href={status.pdfUrl} download className="download-btn-2026">
                      <span className="btn-shine"></span>
                      <span className="btn-content-wrap">
                        <Download size={22} className="btn-icon-download" />
                        <span className="btn-text">Download Your Story Book</span>
                      </span>
                    </a>
                    <style jsx>{`
                      .download-btn-2026 {
                        position: relative;
                        display: inline-flex;
                        align-items: center;
                        gap: 0.75rem;
                        padding: 1.25rem 3rem;
                        font-size: 1.125rem;
                        font-weight: 700;
                        color: white;
                        background: linear-gradient(135deg, 
                          #A8D8E6 0%, 
                          #C9B6E4 25%,
                          #F8C8A0 50%,
                          #C9B6E4 75%,
                          #A8D8E6 100%
                        );
                        background-size: 300% 300%;
                        border: none;
                        border-radius: 60px;
                        cursor: pointer;
                        text-decoration: none;
                        overflow: hidden;
                        box-shadow: 0 8px 32px rgba(168, 216, 230, 0.4),
                                    0 4px 16px rgba(201, 182, 228, 0.3),
                                    0 0 0 0 rgba(168, 216, 230, 0.7);
                        animation: gradientShift 4s ease infinite,
                                   pulseGlow 2s ease-in-out infinite;
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                        isolation: isolate;
                        transform-style: preserve-3d;
                      }

                      @keyframes gradientShift {
                        0%, 100% { background-position: 0% 50%; }
                        50% { background-position: 100% 50%; }
                      }

                      @keyframes pulseGlow {
                        0%, 100% {
                          box-shadow: 0 8px 32px rgba(168, 216, 230, 0.4),
                                      0 4px 16px rgba(201, 182, 228, 0.3),
                                      0 0 0 0 rgba(168, 216, 230, 0.7);
                        }
                        50% {
                          box-shadow: 0 12px 48px rgba(168, 216, 230, 0.6),
                                      0 6px 24px rgba(201, 182, 228, 0.5),
                                      0 0 0 10px rgba(168, 216, 230, 0);
                        }
                      }

                      .download-btn-2026:hover {
                        transform: translateY(-4px) scale(1.02);
                        box-shadow: 0 16px 64px rgba(168, 216, 230, 0.6),
                                    0 8px 32px rgba(201, 182, 228, 0.5),
                                    0 4px 16px rgba(248, 200, 160, 0.4);
                      }

                      .download-btn-2026:active {
                        transform: translateY(-2px) scale(0.98);
                      }

                      .btn-shine {
                        position: absolute;
                        inset: 0;
                        background: linear-gradient(
                          120deg,
                          transparent 0%,
                          transparent 40%,
                          rgba(255, 255, 255, 0.6) 50%,
                          transparent 60%,
                          transparent 100%
                        );
                        background-size: 200% 200%;
                        animation: shineMove 3s ease-in-out infinite;
                        border-radius: 60px;
                        z-index: 0;
                      }

                      @keyframes shineMove {
                        0%, 100% { background-position: -200% 0; }
                        50% { background-position: 200% 0; }
                      }

                      .btn-content-wrap {
                        position: relative;
                        z-index: 1;
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                      }

                      .btn-icon-download {
                        animation: downloadBounce 2s ease-in-out infinite;
                        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
                      }

                      @keyframes downloadBounce {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-4px); }
                      }

                      .btn-text {
                        letter-spacing: 0.02em;
                        text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                      }

                      .download-btn-2026::before {
                        content: '';
                        position: absolute;
                        inset: -3px;
                        background: linear-gradient(135deg, 
                          rgba(168, 216, 230, 0.5), 
                          rgba(201, 182, 228, 0.5),
                          rgba(248, 200, 160, 0.5)
                        );
                        border-radius: 60px;
                        filter: blur(8px);
                        opacity: 0;
                        animation: glowPulse 2s ease-in-out infinite;
                        z-index: -1;
                      }

                      @keyframes glowPulse {
                        0%, 100% { opacity: 0; }
                        50% { opacity: 0.8; }
                      }

                      .download-btn-2026::after {
                        content: '✨';
                        position: absolute;
                        top: -10px;
                        right: -10px;
                        font-size: 1.5rem;
                        animation: sparkleRotate 3s linear infinite;
                        filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.8));
                      }

                      @keyframes sparkleRotate {
                        0%, 100% { 
                          transform: rotate(0deg) scale(1);
                          opacity: 1;
                        }
                        50% { 
                          transform: rotate(180deg) scale(1.2);
                          opacity: 0.7;
                        }
                      }

                      @media (max-width: 768px) {
                        .download-btn-2026 {
                          padding: 1rem 2rem;
                          font-size: 1rem;
                        }
                      }
                    `}</style>
                    {status.watermark && (
                      <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--color-text-tertiary)' }}>
                        This is a watermarked preview. Generate a full book for the complete version!
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {status.status === 'failed' && (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-danger)' }}>
                <p>❌ Generation failed. Please try again.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Books Gallery - Show all user's generated books */}
      <BooksGallery session={session} />

      {/* Featured Audio Books Section */}
      <FeaturedAudioBooks />

      {/* Features Section */}
      <div className="container" style={{ maxWidth: '1200px', padding: '4rem 1.5rem' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '2rem',
          marginBottom: '4rem'
        }}>
          <FeatureCard
            icon={<Palette size={32} />}
            title="AI-Powered Generation"
            description="Advanced AI creates unique, age-appropriate illustrations"
          />
          
          <FeatureCard
            icon={<Heart size={32} />}
            title="Personalized Stories"
            description="Each book is crafted for your child's interests"
          />
          
          <FeatureCard
            icon={<Download size={32} />}
            title="High-Quality PDFs"
            description="Print-ready A4 PDFs at 300 DPI"
          />
        </div>
      </div>

      {/* Pricing Section - Hide completely for subscribers */}
      {!session?.user?.isSubscriber && (
      <div id="pricing-section" className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1.5rem 4rem', scrollMarginTop: '80px' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', marginBottom: '0.75rem' }}>
            {credits > 0 ? 'Upgrade to Unlimited' : 'Simple Pricing'}
          </h2>
          <p style={{ fontSize: '1.125rem', color: 'var(--color-text-secondary)' }}>
            {credits > 0 
              ? 'Get unlimited access to create as many books as you want' 
              : 'Choose the perfect plan for your family or classroom'}
          </p>
        </div>

        {/* Credit Packages - Only show for users without credits */}
        {credits === 0 && (
        <div className="pricing-grid">
          {PRICING_PACKAGES.map((pkg) => (
            <div key={pkg.type} className={`pricing-card ${pkg.featured ? 'featured' : ''}`}>
              {pkg.featured && (
                <div style={{ 
                  background: 'var(--color-bg-gradient-secondary)',
                  color: 'white',
                  padding: '0.5rem',
                  borderRadius: 'var(--radius-full)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  marginBottom: '1rem',
                  textAlign: 'center',
                }}>
                  MOST POPULAR
                </div>
              )}
              
              <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                {pkg.name}
              </h3>
              
              <div className="price">{pkg.price}</div>
              
              <div className="price-label">
                {pkg.credits} Credits
              </div>
              
              <p style={{ 
                fontSize: '0.875rem', 
                color: 'var(--color-text-secondary)',
                marginBottom: '1.5rem' 
              }}>
                {pkg.pricePerBook} per book
              </p>

              <ul style={{ 
                listStyle: 'none', 
                padding: 0, 
                marginBottom: '2rem',
                textAlign: 'left' 
              }}>
                {pkg.features.map((feature, i) => (
                  <li key={i} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    marginBottom: '0.75rem',
                    fontSize: '0.9375rem'
                  }}>
                    <Check size={18} style={{ color: 'var(--color-success)', flexShrink: 0 }} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <style jsx>{`
                .pricing-btn {
                  position: relative;
                  width: 100%;
                  font-size: 1.125rem;
                  font-weight: 700;
                  padding: 1.125rem 2rem;
                  background: linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 50%, #F8C8A0 100%);
                  background-size: 200% 200%;
                  border: none;
                  border-radius: 50px;
                  color: white;
                  cursor: pointer;
                  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                  box-shadow: 0 4px 15px rgba(168, 216, 230, 0.3),
                              0 0 20px rgba(201, 182, 228, 0.2);
                  overflow: hidden;
                  isolation: isolate;
                }

                .pricing-btn::after {
                  content: '';
                  position: absolute;
                  top: -50%;
                  left: -50%;
                  width: 200%;
                  height: 200%;
                  background: linear-gradient(
                    45deg,
                    transparent 30%,
                    rgba(255, 255, 255, 0.3) 50%,
                    transparent 70%
                  );
                  transform: rotate(45deg);
                  animation: shimmerSweep 3s linear infinite;
                  z-index: 0;
                  pointer-events: none;
                }

                @keyframes shimmerSweep {
                  0% { transform: translateX(-100%) rotate(45deg); }
                  100% { transform: translateX(100%) rotate(45deg); }
                }

                .pricing-btn:hover:not(:disabled) {
                  transform: translateY(-3px) scale(1.02);
                  box-shadow: 0 8px 25px rgba(168, 216, 230, 0.5),
                              0 0 35px rgba(201, 182, 228, 0.4);
                  background-position: 100% 50%;
                }

                .pricing-btn:active:not(:disabled) {
                  transform: translateY(-1px) scale(1.01);
                }

                .pricing-btn:disabled {
                  opacity: 0.7;
                  cursor: not-allowed;
                }

                .pricing-btn-text {
                  position: relative;
                  z-index: 1;
                }
              `}</style>
              
              <button
                className="pricing-btn"
                onClick={() => handlePurchase(pkg.type)}
                disabled={loading === pkg.type}
              >
                <span className="pricing-btn-text">
                  {loading === pkg.type ? 'Loading...' : 'Purchase'}
                </span>
              </button>
            </div>
          ))}
        </div>
        )}

        {/* Monthly Subscriptions */}
        <div style={{ marginTop: '3rem' }}>
          <h2 style={{ 
            fontSize: '2rem', 
            fontWeight: 700, 
            textAlign: 'center', 
            marginBottom: '2rem',
            color: 'var(--color-text-primary)'
          }}>
            Monthly Subscriptions
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            marginBottom: '2rem',
          }}>
            {SUBSCRIPTIONS.map((sub) => (
              <div 
                key={sub.id}
                className="card" 
                style={{ 
                  background: sub.featured 
                    ? 'linear-gradient(135deg, rgba(201, 182, 228, 0.1) 0%, rgba(168, 216, 230, 0.1) 100%)'
                    : 'var(--color-bg-primary)',
                  borderColor: sub.featured ? 'var(--color-soft-purple)' : 'var(--color-border)',
                  borderWidth: sub.featured ? '2px' : '1px',
                  position: 'relative',
                  padding: '2rem',
                }}>
                {sub.featured && (
                  <div style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: 'var(--color-soft-purple)',
                    color: 'white',
                    padding: '0.375rem 0.75rem',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                  }}>
                    <Star size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
                    POPULAR
                  </div>
                )}
                
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '70px',
                    height: '70px',
                    background: 'var(--color-bg-gradient-secondary)',
                    borderRadius: 'var(--radius-xl)',
                    marginBottom: '1.5rem',
                  }}>
                    {sub.featured ? <Crown size={35} color="white" /> : <Zap size={35} color="white" />}
                  </div>

                  <h3 style={{ fontSize: '1.75rem', marginBottom: '0.75rem', fontWeight: '700' }}>
                    {sub.name}
                  </h3>

                  <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--color-primary)', margin: '1rem 0' }}>
                    {sub.price.split('/')[0]}<span style={{ fontSize: '1.25rem', fontWeight: 600 }}>/month</span>
                  </div>

                  <p style={{ fontSize: '1rem', marginBottom: '1.5rem', color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                    {sub.description}
                  </p>

                  <ul style={{ 
                    listStyle: 'none', 
                    padding: 0, 
                    marginBottom: '2rem',
                    textAlign: 'left'
                  }}>
                    {sub.features.map((feature, i) => (
                      <li key={i} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.75rem',
                        marginBottom: '0.75rem',
                        fontSize: '0.9375rem'
                      }}>
                        <Check size={18} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    className="btn-primary"
                    onClick={() => handleSubscribe(sub.id as 'amaya_plus' | 'amaya_pro')}
                    disabled={loading === sub.id}
                    style={{ width: '100%', fontSize: '1.125rem' }}
                  >
                    <span>{loading === sub.id ? 'Loading...' : 'Subscribe Now'}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Free Preview Info - Only show for non-paying users */}
        {credits === 0 && (
          <div className="card" style={{ marginTop: '2rem', textAlign: 'center' }}>
            <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>
              ✨ Try It Free First!
            </h3>
            <p style={{ marginBottom: 0 }}>
              Create unlimited free magic books to experience the quality before purchasing. No credit card required!
            </p>
          </div>
        )}
      </div>
      )}
    </div>
    </>
  );
}

// Main export with Suspense wrapper for useSearchParams
export default function HomePage() {
  // Use 2026 UI when feature flag is enabled
  if (SITE_UI_2026) {
    return <HomePage2026 />;
  }

  return (
    <Suspense fallback={<div style={{ minHeight: '100vh', background: 'var(--color-bg-secondary)' }} />}>
      <HomePageContent />
    </Suspense>
  );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="card" style={{ textAlign: 'center' }}>
      <div style={{ 
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '64px',
        height: '64px',
        background: 'var(--color-bg-gradient-primary)',
        borderRadius: 'var(--radius-lg)',
        marginBottom: '1.5rem',
        color: 'var(--color-warm-gray)'
      }}>
        {icon}
      </div>
      <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.75rem' }}>
        {title}
      </h3>
      <p style={{ color: 'var(--color-text-secondary)', marginBottom: 0 }}>
        {description}
      </p>
    </div>
  );
}

